// src/publish/pieces/index.js — a history entry → its publishable pieces.
//
//   piecesForEntry(entry) → pieces[]   dispatches on entry.results.contentFormat
//                                      and entry.results.platform (same keys the
//                                      backend uses to compose full_text):
//     contentFormat 'carousel'       → linkedinCarousel
//                   'seo'            → blogSeo
//                   'newsletter'     → linkedinNewsletter
//                   'email-classic'  → emailClassic
//                   'landing'        → landingPage
//     platform 'instagram' | 'facebook' | 'pinterest' | 'youtube' → that builder
//     platform 'linkedin' + contentFormat 'post'                   → linkedinPost
//     anything else (legacy object, a string content)             → generic
//   A cancelled entry or one without content gives []. A builder that returns
//   something other than an array is treated as [].
//   builderKeyFor(entry) → the format key of THAT builder ('blog-seo',
//                          'linkedin-post', …, 'generic'): the one key the
//                          download catalogue (../downloads.js) reads, so an
//                          entry with platform null and contentFormat 'seo'
//                          gets the blog's pieces AND the blog's files.
//   The piece list is CACHED per entry object and UI language (a WeakMap of
//   entry → Map<language, pieces[]>): history entries are immutable, so the
//   same object always gives the same array (callers never mutate it), and a
//   language change builds anew because labels and destinations are resolved
//   through i18n at build time. Nothing invalidates a cached entry.
//
// Each builder exports build(entry) → pieces[] (see ../piece.js :: makePiece
// for the shape and the i18n convention) and owns one format; this file never
// looks inside `content`. Also here: the piece-level conveniences every caller
// (copy button, download menu, counters) needs —
//   primaryPiece(pieces)   the one piece with primary: true (else the first)
//   pieceText(piece, {cites})  plain text (cites dropped unless asked)
//   pieceHtml(piece)       clean HTML fragment of its blocks
//   pieceMarkdown(piece)   Markdown of its blocks
//   pieceLength(piece)     characters of the plain text, cites dropped, counted
//                          in code points (an emoji is one)

import { build as linkedinPost } from './linkedinPost.js';
import { build as facebookPost } from './facebookPost.js';
import { build as instagramPost } from './instagramPost.js';
import { build as pinterestPost } from './pinterestPost.js';
import { build as youtubePost } from './youtubePost.js';
import { build as linkedinCarousel } from './linkedinCarousel.js';
import { build as linkedinNewsletter } from './linkedinNewsletter.js';
import { build as blogSeo } from './blogSeo.js';
import { build as emailClassic } from './emailClassic.js';
import { build as landingPage } from './landingPage.js';
import { build as generic } from './generic.js';
import i18n from '../../i18n/index.js';
import { blocksToPlain } from '../serialize/plain.js';
import { blocksToHtml } from '../serialize/html.js';
import { blocksToMarkdown } from '../serialize/markdown.js';

const BY_FORMAT = {
  carousel: linkedinCarousel,
  seo: blogSeo,
  newsletter: linkedinNewsletter,
  'email-classic': emailClassic,
  landing: landingPage,
};

const BY_PLATFORM = {
  instagram: instagramPost,
  facebook: facebookPost,
  pinterest: pinterestPost,
  youtube: youtubePost,
};

// The format key each builder owns (the FORMAT constant of its module).
const KEY_OF_BUILDER = new Map([
  [linkedinPost, 'linkedin-post'], [facebookPost, 'facebook-post'], [instagramPost, 'instagram-post'],
  [pinterestPost, 'pinterest-post'], [youtubePost, 'youtube-post'], [linkedinCarousel, 'linkedin-carousel'],
  [linkedinNewsletter, 'linkedin-newsletter'], [blogSeo, 'blog-seo'], [emailClassic, 'email-email-classic'],
  [landingPage, 'landing-landing'], [generic, 'generic'],
]);

/** The builder an entry dispatches to (exported for the tests; callers use piecesForEntry). */
export function builderFor(entry) {
  const r = entry?.results || {};
  const cf = typeof r.contentFormat === 'string' ? r.contentFormat : '';
  const platform = typeof r.platform === 'string' ? r.platform : '';
  if (BY_FORMAT[cf]) return BY_FORMAT[cf];
  if (BY_PLATFORM[platform]) return BY_PLATFORM[platform];
  if (platform === 'linkedin' && cf === 'post') return linkedinPost;
  return generic;
}

/** The format key of the builder an entry dispatches to ('generic' for the fallback). */
export function builderKeyFor(entry) {
  return KEY_OF_BUILDER.get(builderFor(entry)) || 'generic';
}

const PIECES_CACHE = new WeakMap();   // entry → Map<language, pieces[]>

function buildPieces(entry) {
  const r = entry?.results;
  if (!r || r.cancelled || r.status === 'cancelled' || r.content == null || r.content === '') return [];
  const out = builderFor(entry)(entry);
  return Array.isArray(out) ? out.filter(Boolean) : [];
}

export function piecesForEntry(entry) {
  if (!entry || typeof entry !== 'object') return [];
  const language = typeof i18n.language === 'string' ? i18n.language : '';
  let byLanguage = PIECES_CACHE.get(entry);
  if (!byLanguage) { byLanguage = new Map(); PIECES_CACHE.set(entry, byLanguage); }
  if (byLanguage.has(language)) return byLanguage.get(language);
  const pieces = buildPieces(entry);
  byLanguage.set(language, pieces);
  return pieces;
}

export function primaryPiece(pieces) {
  const list = Array.isArray(pieces) ? pieces.filter(Boolean) : [];
  return list.find((p) => p.primary) || list[0] || null;
}

export function pieceText(piece, { cites = false } = {}) {
  return piece ? blocksToPlain(piece.blocks, { cites }) : '';
}

export function pieceHtml(piece) {
  return piece ? blocksToHtml(piece.blocks) : '';
}

export function pieceMarkdown(piece) {
  return piece ? blocksToMarkdown(piece.blocks) : '';
}

export function pieceLength(piece) {
  return Array.from(pieceText(piece)).length;
}
